/**
 * Texto legible de las asignaciones instrumento+voz de un músico (API con `instrumentosAsignados`).
 */
export function formatMusicoInstrumentosText(musico, sep = ', ') {
  const rows = musico?.instrumentosAsignados;
  if (!rows?.length) return '-';
  return rows
    .map((r) => {
      const n = r.instrumento?.nombre || r.instrumentoPersonalizado?.nombre || '?';
      const v = r.vozPartituraPersonalizada?.nombre;
      return v ? `${n} (${v})` : n;
    })
    .join(sep);
}

/** Solo nombres de instrumento (sin voz de partitura); útil en micrositio público. */
export function formatMusicoInstrumentosSinVoz(musico, sep = ', ') {
  const rows = musico?.instrumentosAsignados;
  if (!rows?.length) return '—';
  return rows
    .map((r) => r.instrumento?.nombre || r.instrumentoPersonalizado?.nombre || '?')
    .join(sep);
}

/** Familias distintas presentes en las asignaciones (para filtros). */
export function musicoFamiliasSet(musico) {
  const s = new Set();
  for (const r of musico?.instrumentosAsignados || []) {
    const f = r.instrumento?.familia || r.instrumentoPersonalizado?.familia;
    if (f) s.add(f);
  }
  return s;
}

/** Primera familia (p. ej. color de badge cuando hay varias). */
export function primeraFamiliaMusico(musico) {
  const r = musico?.instrumentosAsignados?.[0];
  return r?.instrumento?.familia || r?.instrumentoPersonalizado?.familia || null;
}

/** Orden de familias en plantilla pública del micrositio (después del director). */
const FAMILIA_RANK_PLANTILLA = {
  VIENTO_MADERA: 1,
  VIENTO_METAL: 2,
  PERCUSION: 3,
  CUERDA: 4,
  VOZ: 5,
  OTRO: 6,
};

function nombreAsignacionRow(r) {
  return (r?.instrumento?.nombre || r?.instrumentoPersonalizado?.nombre || '').trim();
}

/** Detecta director/a por nombre de instrumento (catálogo o personalizado). */
export function isMusicoDirectorPlantilla(musico) {
  for (const r of musico?.instrumentosAsignados || []) {
    const n = nombreAsignacionRow(r);
    if (!n) continue;
    const low = n
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{M}/gu, '');
    if (low === 'director' || low === 'directora') return true;
    if (low.startsWith('director ') || low.startsWith('directora ')) return true;
  }
  return false;
}

function nombreCompletoMusico(m) {
  return `${m?.nombre || ''} ${m?.apellidos || ''}`.trim();
}

/**
 * Primera asignación no-director (por `orden`) para ordenar por familia en micrositio.
 */
function metaOrdenPlantillaNoDirector(musico) {
  for (const r of musico?.instrumentosAsignados || []) {
    const n = nombreAsignacionRow(r);
    if (!n) continue;
    const low = n
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{M}/gu, '');
    if (low === 'director' || low === 'directora') continue;
    if (low.startsWith('director ') || low.startsWith('directora ')) continue;
    const fam = r.instrumento?.familia || r.instrumentoPersonalizado?.familia;
    const ordenInst = typeof r.instrumento?.orden === 'number' ? r.instrumento.orden : 9999;
    if (fam) return { fam, ordenInst, nombreInst: n };
  }
  return { fam: 'OTRO', ordenInst: 9999, nombreInst: '' };
}

function rankFamilia(fam) {
  return FAMILIA_RANK_PLANTILLA[fam] ?? 99;
}

/**
 * Orden micrositio: directores primero; luego madera → metal → percusión → cuerda → voz → otro.
 */
export function sortMusicosPlantillaMicrosite(musicos) {
  const list = [...(musicos || [])].filter(Boolean);
  const directores = list.filter(isMusicoDirectorPlantilla);
  const resto = list.filter((m) => !isMusicoDirectorPlantilla(m));
  directores.sort((a, b) =>
    nombreCompletoMusico(a).localeCompare(nombreCompletoMusico(b), 'es', { sensitivity: 'base' }),
  );
  resto.sort((a, b) => {
    const ma = metaOrdenPlantillaNoDirector(a);
    const mb = metaOrdenPlantillaNoDirector(b);
    const ra = rankFamilia(ma.fam);
    const rb = rankFamilia(mb.fam);
    if (ra !== rb) return ra - rb;
    if (ma.ordenInst !== mb.ordenInst) return ma.ordenInst - mb.ordenInst;
    const cmpInst = ma.nombreInst.localeCompare(mb.nombreInst, 'es', { sensitivity: 'base' });
    if (cmpInst !== 0) return cmpInst;
    return nombreCompletoMusico(a).localeCompare(nombreCompletoMusico(b), 'es', { sensitivity: 'base' });
  });
  return [...directores, ...resto];
}

/** ¿Algún músico activo tiene más de una fila en plantilla? (editor Tu web / micrositio). */
export function hayMusicosActivosConVariosInstrumentos(musicos) {
  const list = Array.isArray(musicos) ? musicos : [];
  return list.some((m) => m && m.activo !== false && (m.instrumentosAsignados?.length || 0) > 1);
}

/** Etiqueta de una fila de asignación (instrumento + voz opcional). */
export function labelAsignacionInstrumentoRow(r) {
  const n = r?.instrumento?.nombre || r?.instrumentoPersonalizado?.nombre || '?';
  const v = r?.vozPartituraPersonalizada?.nombre;
  return v ? `${n} (${v})` : n;
}

/**
 * Copia superficial del músico con `instrumentosAsignados` filtrados para la plantilla pública.
 * @param {'todos'|'uno'} modo
 * @param {Record<string,string>} instrumentoPorMusico musicoId → id de fila MusicoInstrumento
 */
export function musicoConAsignacionesParaPlantillaPublica(musico, modo, instrumentoPorMusico) {
  const rows = [...(musico?.instrumentosAsignados || [])];
  if (!rows.length) return { ...musico, instrumentosAsignados: [] };
  if (modo !== 'uno' || rows.length <= 1) {
    return { ...musico, instrumentosAsignados: rows };
  }
  const pickId = instrumentoPorMusico?.[musico.id];
  let chosen = pickId ? rows.find((r) => r.id === pickId) : null;
  if (!chosen) {
    chosen =
      rows.find((r) => {
        const n = (r?.instrumento?.nombre || r?.instrumentoPersonalizado?.nombre || '').trim();
        if (!n) return false;
        const low = n
          .toLowerCase()
          .normalize('NFD')
          .replace(/\p{M}/gu, '');
        if (low === 'director' || low === 'directora') return false;
        if (low.startsWith('director ') || low.startsWith('directora ')) return false;
        return true;
      }) || rows[0];
  }
  return { ...musico, instrumentosAsignados: chosen ? [chosen] : rows };
}

export function musicosPreparadosPlantillaMicrositio(musicos, modo, instrumentoPorMusico) {
  const list = Array.isArray(musicos) ? musicos.filter(Boolean) : [];
  return list.map((m) => musicoConAsignacionesParaPlantillaPublica(m, modo, instrumentoPorMusico || {}));
}
